'use client';

import { cn, CustomError } from '@core/utils';
import { createContext, useContext, useId } from 'react';

import { useSubscribedState } from '../../../hooks';
import type { HTMLTagProps } from '../../../types';

type RadioValue = string | number;

type RadioTheme = 'blue' | 'black';

interface RadioGroupProps<T extends RadioValue> extends Pick<
  HTMLTagProps<'fieldset'>,
  'children' | 'className' | 'name'
> {
  onChange?: (value: T) => void;
  value?: T;
  readOnly?: boolean;
  theme?: RadioTheme;
}

type RadioContextValue = {
  name: string;
  onChange?: (value: RadioValue) => void;
  value?: RadioValue;
  readOnly?: boolean;
  theme: RadioTheme;
};

const RadioContext = createContext<RadioContextValue | null>(null);

const Group = <T extends RadioValue>({
  value: defaultValue,
  onChange,
  children,
  readOnly,
  className,
  name,
  theme = 'blue',
}: RadioGroupProps<T>) => {
  const generatedId = useId();
  const resolvedName = name ?? generatedId;
  const [value, setValue] = useSubscribedState<T | undefined>(defaultValue);

  return (
    <fieldset className={cn('flex gap-8 typo-body4', className)}>
      <RadioContext.Provider
        value={{
          name: resolvedName,
          onChange: (nextValue) => {
            if (readOnly) return;
            setValue(nextValue as T);
            onChange?.(nextValue as T);
          },
          value,
          readOnly,
          theme,
        }}
      >
        {children}
      </RadioContext.Provider>
    </fieldset>
  );
};

interface RadioItemProps extends Omit<HTMLTagProps<'input'>, 'children'> {
  className?: string;
  value: string | number;
  children?: React.ReactNode;
}

const Item = ({ className, children, value, disabled }: RadioItemProps) => {
  const context = useContext(RadioContext);

  if (!context)
    throw new CustomError({
      return_message: 'Radio.Item은 Radio.Group 내부에서만 사용할 수 있습니다.',
    });

  const { value: groupValue, onChange, readOnly, theme } = context;

  const inputClassName = (() => {
    const COMMON_CLASS_NAME =
      'size-18 rounded-pill box-border appearance-none border-1 border-Gray-300 checked:border-4 bg-Gray-white group-hover:bg-Gray-50 transition-colors duration-200 m-3';

    if (readOnly) {
      switch (theme) {
        case 'black':
          return `${COMMON_CLASS_NAME} checked:border-Gray-black cursor-not-allowed`;

        case 'blue':
        default:
          return `${COMMON_CLASS_NAME} checked:border-B-300 cursor-not-allowed`;
      }
    }

    switch (theme) {
      case 'black':
        return `${COMMON_CLASS_NAME} checked:border-Gray-black`;

      case 'blue':
      default:
        return `${COMMON_CLASS_NAME} checked:border-B-500`;
    }
  })();

  return (
    <label
      className={cn(
        'group flex cursor-pointer items-center justify-center gap-4',
        { 'cursor-not-allowed': readOnly },
        className,
      )}
    >
      <input
        checked={groupValue === value}
        className={cn(inputClassName)}
        disabled={readOnly || disabled}
        name={context.name}
        type="radio"
        value={value}
        onChange={(e) => {
          if (e.target.checked && !disabled) {
            onChange?.(value);
          }
        }}
      />
      {children}
    </label>
  );
};

export const Radio = {
  Group,
  Item,
};
